import * as React from 'react';
import { AppContext } from '../AppContext';
import { ToastContext } from './Toast';
import { getDisplayedIds, moveItem, MoveDirection, swapWithNeighbor } from '../utils/reorder';
import './ReorderMode.css';

// ドラッグの取っ手を表す 6 点のグリップ
const GripIcon: React.FC = function () {
  return (
    <svg className="ReorderMode-grip_icon" width="12" height="20" viewBox="0 0 12 20" aria-hidden="true">
      <circle cx="3" cy="4" r="1.8" />
      <circle cx="9" cy="4" r="1.8" />
      <circle cx="3" cy="10" r="1.8" />
      <circle cx="9" cy="10" r="1.8" />
      <circle cx="3" cy="16" r="1.8" />
      <circle cx="9" cy="16" r="1.8" />
    </svg>
  );
};

/** 並びを変えたきっかけ。取っ手を落としたときと、取っ手を選んで矢印キーを押したときで、保存の時機を変える */
export type ReorderSource = 'drop' | 'key';

/** 行の左端の取っ手のセルに渡す値。取っ手の動きは useRowReorder が作る */
export interface ReorderHandleCellProps {
  /** 保存の途中や絞り込みの間など、並びを変えさせたくないときに true にし、取っ手を出さない */
  disabled?: boolean;
  onDragStart: (event: React.DragEvent<HTMLDivElement>) => void;
  onDragEnd: () => void;
  onMove: (direction: MoveDirection) => void;
}

// 取っ手を選んだ状態で押したキーを、行を動かす向きに読み替える。行を動かすキーでなければ null
function toMoveDirection(key: string): MoveDirection | null {
  if (key === 'ArrowUp') {
    return 'up';
  }
  if (key === 'ArrowDown') {
    return 'down';
  }
  return null;
}

/**
 * 行の左端に置く、取っ手のセル。
 * 取っ手はマウスでつかんで別の行へ落とすほか、キーボードで取っ手を選んで上下の矢印キーを押すと、隣の行と入れ替わる。
 * 行は見分け方をkeyにして描き直すので、入れ替えた後も同じ取っ手が選ばれたままになり、続けて動かせる
 */
export const ReorderHandleCell: React.FC<ReorderHandleCellProps> = function (props) {
  // 並びを変えさせないときは、列の幅をそろえるために空のセルだけを置く
  if (props.disabled === true) {
    return <td className="ReorderMode-handle_cell" />;
  }
  return (
    <td className="ReorderMode-handle_cell">
      <div
        className="ReorderMode-drag_handle"
        role="button"
        tabIndex={0}
        aria-label="並び替えの取っ手（上下の矢印キーで移動）"
        draggable={true}
        title="ドラッグして並び替え"
        onDragStart={props.onDragStart}
        onDragEnd={props.onDragEnd}
        onKeyDown={(event) => {
          const direction = toMoveDirection(event.key);
          if (direction === null) {
            return;
          }
          // 矢印キーで画面がスクロールしないよう、ブラウザの既定の動きを止める
          event.preventDefault();
          props.onMove(direction);
        }}
      >
        <GripIcon />
      </div>
    </td>
  );
};

/** 取っ手の列の見出し。見出しは空にし、取っ手が収まる幅のまま変えられないようにする */
export const ReorderHandleHeader: React.FC = function () {
  return <th className="ReorderMode-handle_cell" data-column-resize="off" aria-label="並び替え" />;
};

/** 並びを入れ替えられる表の、行に付ける属性。行へドラッグしてきた取っ手を受け止め、つかんでいる行を薄くする */
export interface ReorderRowProps {
  onDragOver: (event: React.DragEvent<HTMLTableRowElement>) => void;
  onDrop: (event: React.DragEvent<HTMLTableRowElement>) => void;
  style: React.CSSProperties;
}

export interface RowReorder<K> {
  /** 行（tr）に広げて付ける属性 */
  getRowProps: (key: K) => ReorderRowProps;
  /** 行の左端の ReorderHandleCell に広げて渡す値 */
  getHandleCellProps: (key: K, index: number) => ReorderHandleCellProps;
}

/**
 * 表の行を、取っ手のドラッグと、取っ手を選んだ状態の上下の矢印キーで入れ替えるフック。
 * keys は表に出している順の行の見分け方で、並びを変えるたびに、変えた後の並びと変えたきっかけを onChange に渡す。
 * 並びそのものは呼び出し側が持ち、このフックはドラッグしている行だけを持つ
 */
export function useRowReorder<K>(
  keys: K[],
  onChange: (nextKeys: K[], source: ReorderSource) => void
): RowReorder<K> {
  // つかんでいる行。何もつかんでいないときは null
  const [dragSourceKey, setDragSourceKey] = React.useState<K | null>(null);

  function handleDrop(targetKey: K) {
    if (dragSourceKey !== null) {
      const sourceIndex = keys.indexOf(dragSourceKey);
      const targetIndex = keys.indexOf(targetKey);
      if (sourceIndex >= 0 && targetIndex >= 0 && sourceIndex !== targetIndex) {
        onChange(moveItem(keys, sourceIndex, targetIndex), 'drop');
      }
    }
    setDragSourceKey(null);
  }

  function getRowStyle(key: K): React.CSSProperties {
    if (dragSourceKey !== null && dragSourceKey === key) {
      return { opacity: 0.5 };
    }
    return {};
  }

  return {
    getRowProps(key) {
      return {
        // 行の上へ落とせるように、ブラウザの「落とせない」という既定の扱いを止める
        onDragOver(event) {
          event.preventDefault();
        },
        onDrop(event) {
          event.preventDefault();
          handleDrop(key);
        },
        style: getRowStyle(key),
      };
    },
    getHandleCellProps(key, index) {
      return {
        onDragStart(event) {
          event.dataTransfer.setData('text/plain', String(key));
          setDragSourceKey(key);
        },
        onDragEnd() {
          setDragSourceKey(null);
        },
        onMove(direction) {
          const nextKeys = swapWithNeighbor(keys, index, direction);
          onChange(nextKeys, 'key');
        },
      };
    },
  };
}

/** 矢印キーで動かしたときに、最後に押してから保存するまで待つ時間（ミリ秒）。続けて押した分を1回の保存にまとめる */
const KEY_MOVE_SAVE_DELAY = 800;
/** 「元に戻す」を押せるよう、保存の通知をふだんより長く出す時間（ミリ秒） */
const UNDO_TOAST_TIMEOUT = 8000;
const SAVE_FAILED_MESSAGE = '並び順を保存できませんでした。時間をおいて再実行してください。';

export interface ListReorderOptions {
  /** 並びを変えられる権限の段階。一覧の編集のボタンと同じ段階にする */
  authLv: number;
  /** 絞り込んでいる間は true。一部の行だけを並べ替えると、隠れている行がどこに入るのか分からなくなるので、並びを変えさせない */
  filtering: boolean;
  /** 並び順をサーバーへ保存する。失敗したときは例外を投げる */
  save: (ids: number[]) => Promise<unknown>;
}

export interface ListReorder extends RowReorder<number> {
  /** 表に出す並び順 */
  displayedIds: number[];
  /** 取っ手の列を置くか。並びを変えられる権限の人だけ true */
  handleColumn: boolean;
  /** 取っ手を出して並びを変えられるか。権限があり、2件以上あり、絞り込んでおらず、保存の途中でないときだけ true */
  enabled: boolean;
  /** 権限があるのに、絞り込んでいるために並びを変えられないとき true。その旨を件数の横に知らせる */
  pausedByFilter: boolean;
}

/**
 * 一覧の行を、取っ手で動かしてすぐに保存するフック。
 * 取っ手を落としたらその時点で保存し、矢印キーで動かしたときは押し終えてから1回にまとめて保存する。
 * 保存できたら「元に戻す」付きの通知を出し、押すと動かす前の並びを保存し直す。
 * serverIds はサーバーが返した並び順の id。データを読み込む前は空の配列を渡す
 */
export function useListReorder(serverIds: number[], options: ListReorderOptions): ListReorder {
  const appContext = React.useContext(AppContext);
  const toastContext = React.useContext(ToastContext);
  // 画面の中で動かした並び。保存の後もサーバーから読み込み直さずにこの並びを出し、null のときはサーバーの並びを出す
  const [draftIds, setDraftIds] = React.useState<number[] | null>(null);
  const [saving, setSaving] = React.useState(false);
  const displayedIds = getDisplayedIds(serverIds, draftIds);
  // サーバーに保存されているとわかっている並び。保存に失敗したときは、この並びへ戻す
  const savedIdsRef = React.useRef<number[]>(serverIds);
  // 矢印キーで続けて動かしている間の、動かし始める前の並びと、最後の並び
  const keyMoveStartRef = React.useRef<number[] | null>(null);
  const keyMoveLatestRef = React.useRef<number[]>([]);
  const keyMoveTimerRef = React.useRef<number | null>(null);
  const mountedRef = React.useRef(true);

  // サーバーから並びを読み込み直したら、画面の中の並びを捨ててサーバーの並びを出す
  const serverKey = serverIds.join(',');
  React.useEffect(() => {
    savedIdsRef.current = serverKey.split(',').filter((id) => id !== '').map(Number);
    setDraftIds(null);
  }, [serverKey]);

  React.useEffect(() => {
    return () => {
      mountedRef.current = false;
      if (keyMoveTimerRef.current !== null) {
        window.clearTimeout(keyMoveTimerRef.current);
      }
    };
  }, []);

  const save = options.save;
  const setSavingIfMounted = (value: boolean) => {
    if (mountedRef.current) {
      setSaving(value);
    }
  };
  const revertToSaved = () => {
    if (mountedRef.current) {
      setDraftIds(savedIdsRef.current);
    }
  };

  // 動かす前の並びへ戻して保存し直す
  const undo = async (previousIds: number[]) => {
    if (mountedRef.current) {
      setDraftIds(previousIds);
    }
    setSavingIfMounted(true);
    try {
      await save(previousIds);
      savedIdsRef.current = previousIds;
      toastContext.showToast('並び順を元に戻しました', 'success');
    } catch {
      toastContext.showToast(SAVE_FAILED_MESSAGE, 'error');
      revertToSaved();
    } finally {
      setSavingIfMounted(false);
    }
  };

  const commit = async (nextIds: number[], previousIds: number[]) => {
    setSavingIfMounted(true);
    try {
      await save(nextIds);
      savedIdsRef.current = nextIds;
      toastContext.showToast('並び順を保存しました', 'success', UNDO_TOAST_TIMEOUT, {
        label: '元に戻す',
        onClick: () => {
          undo(previousIds);
        },
      });
    } catch {
      toastContext.showToast(SAVE_FAILED_MESSAGE, 'error');
      revertToSaved();
    } finally {
      setSavingIfMounted(false);
    }
  };

  const rowReorder = useRowReorder(displayedIds, (nextIds, source) => {
    setDraftIds(nextIds);
    if (source === 'drop') {
      commit(nextIds, displayedIds);
      return;
    }
    if (keyMoveStartRef.current === null) {
      keyMoveStartRef.current = displayedIds;
    }
    keyMoveLatestRef.current = nextIds;
    if (keyMoveTimerRef.current !== null) {
      window.clearTimeout(keyMoveTimerRef.current);
    }
    keyMoveTimerRef.current = window.setTimeout(() => {
      const startIds = keyMoveStartRef.current ?? savedIdsRef.current;
      keyMoveStartRef.current = null;
      keyMoveTimerRef.current = null;
      commit(keyMoveLatestRef.current, startIds);
    }, KEY_MOVE_SAVE_DELAY);
  });

  const me = appContext.state.me;
  const handleColumn = me !== undefined && me !== null && me.authority.level >= options.authLv;
  const hasEnoughRows = serverIds.length >= 2;
  const enabled = handleColumn && hasEnoughRows && !options.filtering && !saving;

  return {
    getRowProps: rowReorder.getRowProps,
    getHandleCellProps(key, index) {
      return {
        ...rowReorder.getHandleCellProps(key, index),
        disabled: !enabled,
      };
    },
    displayedIds,
    handleColumn,
    enabled,
    pausedByFilter: handleColumn && hasEnoughRows && options.filtering,
  };
}

/** 表の列数。並びを変えられる人には、左端の取っ手の列の分を足す */
export function getTableColumnCount(reorder: ListReorder, baseCount: number): number {
  if (reorder.handleColumn) {
    return baseCount + 1;
  }
  return baseCount;
}

/**
 * 表の列の幅の保存先。取っ手の列があるかどうかで列の並びが違うので、列の幅は別々に保存する。
 * TableScroll の key にも使い、切り替わったときに作り直す
 */
export function getReorderTableStorageKey(reorder: ListReorder, storageKey: string): string {
  if (reorder.handleColumn) {
    return `${storageKey}Reorder`;
  }
  return storageKey;
}
